// src/hooks/useFocusTrap.ts
//
// Keep keyboard focus inside a dialog while it is open.
//
// Tab from the last focusable control wraps to the first, Shift+Tab from the
// first wraps to the last, and focus that has somehow landed outside is pulled
// back in. Without this a keyboard user tabs straight out of an open modal into
// the page behind it — controls they can neither see nor are meant to use.
//
// The listener is attached to the container itself with native
// `addEventListener`, not React's `onKeyDown`, so a modal opened from inside
// another (both portaled to <body>) traps only its own focus: a nested
// dialog's key events never reach the outer one's DOM node.

import { useEffect, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

export function useFocusTrap(ref: RefObject<HTMLElement>, active: boolean): void {
  useEffect(() => {
    const container = ref.current;
    if (!active || !container) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const items = focusables(container);
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement as HTMLElement | null;
      const inside = !!current && container.contains(current);

      if (e.shiftKey && (current === first || !inside || current === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', onKeyDown);
    return () => container.removeEventListener('keydown', onKeyDown);
  }, [ref, active]);
}
